import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '@app/store/utils/baseQueryWithReauth';
import { ApiResponse, getUniqueByLowestId } from '@app/store/utils/common';
import { updateBalance } from '@app/store/utils/cacheUpdate';
import { invalidateOnSuccess, sortByKey } from '@app/shared/utils/common';
import {
  BuyStoneBody,
  BuyStoneResponse,
  EnergyStoneModel,
  StoneTagType,
} from '@app/store/slices/stones/stoneTypes';
import { BalanceActionType, PriceType } from '@app/shared/types/common';
import { TaskTagTypes } from '@app/store/slices/tasks/taskTypes';
import { tasksApi } from '@app/store/slices/tasks/tasksApi';

export const stonesApi = createApi({
  reducerPath: 'stonesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: Object.values(StoneTagType),
  endpoints: (builder) => ({
    getStones: builder.query<ApiResponse<EnergyStoneModel[]>, void>({
      query: () => ({
        url: '/stones',
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<EnergyStoneModel[]>) => {
        const sortedData = sortByKey(response.data ?? [], 'price');
        const uniqueStones = getUniqueByLowestId(
          sortedData,
          (item) => `${item.name}-${String(item.nodeLevel)}`
        );
        return { ...response, data: uniqueStones };
      },
      providesTags: [StoneTagType.STONES],
    }),
    buyStone: builder.mutation<
      ApiResponse<BuyStoneResponse>,
      BuyStoneBody & { stonePrice: number }
    >({
      query: ({ stoneId }) => ({
        url: '/stones',
        method: 'POST',
        body: { stoneId },
      }),
      invalidatesTags: invalidateOnSuccess([StoneTagType.STONES]),
      onQueryStarted({ stonePrice }, { queryFulfilled, dispatch }) {
        void queryFulfilled.then(() => {
          updateBalance(PriceType.COOKIE, BalanceActionType.DECREASE, stonePrice, dispatch);
          dispatch(tasksApi.util.invalidateTags([TaskTagTypes.TASKS]));
        });
      },
    }),
  }),
});

export const { useGetStonesQuery, useBuyStoneMutation } = stonesApi;
